import type {
  HistoryResponse,
  HistorySeason,
  LeagueBundle,
  LeagueInfo,
  PickAsset,
  Player,
  PlayersFile,
  Team,
  TeamRecord,
} from '../../shared/types';
import { positionRank, winPct } from './format';

/** Everything the UI needs to render a team's identity in one place. */
export interface TeamView {
  roster_id: number;
  /** Custom team name if set, else the manager's Sleeper handle. */
  name: string;
  manager: string;
  avatar: string | null;
  record: TeamRecord;
  team: Team;
}

export function buildTeamViews(bundle: LeagueBundle): TeamView[] {
  const managers = new Map(bundle.managers.map((m) => [m.user_id, m]));
  return bundle.teams.map((t) => {
    const m = t.owner_id ? managers.get(t.owner_id) : undefined;
    return {
      roster_id: t.roster_id,
      name: m?.team_name || m?.display_name || `Roster ${t.roster_id}`,
      manager: m?.display_name ?? 'Unclaimed',
      avatar: m?.avatar ?? null,
      record: t.record,
      team: t,
    };
  });
}

export function teamIndex(views: TeamView[]): Map<number, TeamView> {
  return new Map(views.map((v) => [v.roster_id, v]));
}

/**
 * Roster id -> team, for a season that has already finished.
 *
 * Read from that season's OWN placements rather than from the current roster
 * list, which is what makes an archived page show the names people were using
 * that year. It is also the only correct option: Sleeper renumbers rosters
 * every August, so the current season's roster 3 is not last season's roster 3,
 * and reusing today's map would attach the right names to the wrong teams --
 * a page of plausible results that is quietly all wrong.
 */
export function historyTeamIndex(season: HistorySeason): Map<number, TeamView> {
  const map = new Map<number, TeamView>();
  for (const p of season.placements) {
    map.set(p.roster_id, {
      roster_id: p.roster_id,
      name: p.team_name ?? p.display_name,
      manager: p.display_name,
      avatar: p.avatar,
      record: p.record,
      team: {
        roster_id: p.roster_id,
        owner_id: p.owner_id,
        players: [],
        starters: [],
        reserve: [],
        taxi: [],
        record: p.record,
      },
    });
  }
  return map;
}

/**
 * Standings order. Sleeper itself ranks on record with points-for as the
 * tiebreak, so we match that rather than inventing our own.
 */
export function sortStandings(views: TeamView[]): TeamView[] {
  return [...views].sort(
    (a, b) => winPct(b.record) - winPct(a.record) || b.record.pointsFor - a.record.pointsFor,
  );
}

/** True when no games have been played yet -- drives the offseason UI. */
export function isPreseason(bundle: LeagueBundle): boolean {
  return bundle.teams.every((t) => t.record.wins + t.record.losses + t.record.ties === 0);
}

// --- The league's own calendar ----------------------------------------------

/**
 * How many weeks the playoff bracket occupies.
 *
 * Sleeper does not report a round count, only how many teams qualify, so it has
 * to be derived: a single-elimination bracket needs ceil(log2(n)) rounds, and
 * the byes that a non-power-of-two field produces occupy a round rather than
 * skipping one. This league's six qualifiers give 3 (two byes, then semis, then
 * the final), which is what the previously hard-coded "+2" and "17 weeks"
 * happened to encode.
 *
 * Assumes one week per round. Sleeper can be configured for two-week rounds
 * (`playoff_round_type`), which we do not read -- but the assumption was
 * already baked into every caller, and it is now in exactly one place.
 */
export function playoffRounds(playoffTeams: number): number {
  return Math.max(1, Math.ceil(Math.log2(Math.max(2, playoffTeams))));
}

/**
 * The last week the league's own schedule can reach.
 *
 * Sleeper keeps scoring lineups past this point -- 2025 week 18 came back with
 * all ten rosters scoring and `matchup_id: null` on every row -- so anything
 * that enumerates weeks has to stop here rather than at the NFL's calendar.
 */
export function lastLeagueWeek(league: LeagueInfo): number {
  return league.playoff_week_start + playoffRounds(league.playoff_teams) - 1;
}

/**
 * The first season this league ever played.
 *
 * `history` holds every COMPLETED season, so the oldest of those is the
 * inaugural year -- unless the league is in its first season and has none, in
 * which case the season in progress is the answer. Taking the minimum across
 * both covers the gap without a special case, and returns null while history is
 * still in flight so a caller can withhold the fact rather than print the
 * current year and correct itself a moment later.
 */
export function inauguralSeason(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
): string | null {
  if (!history) return null;
  const years = [bundle.league.season, ...history.seasons.map((s) => s.season)]
    .map(Number)
    .filter((n) => Number.isFinite(n));
  return years.length > 0 ? String(Math.min(...years)) : null;
}

// --- Players ---------------------------------------------------------------

export function lookupPlayer(file: PlayersFile | null, id: string): Player {
  const row = file?.players[id];
  if (!row) {
    // Sleeper occasionally keeps a roster id for a player it has since purged
    // from the dictionary (mostly camp bodies). Show the id rather than a gap.
    return { id, name: `Player ${id}`, position: '?', team: null, age: null, yearsExp: null, injuryStatus: null };
  }
  const [name, position, team, age, yearsExp, injuryStatus] = row;
  return { id, name, position, team, age, yearsExp, injuryStatus };
}

export function sortPlayers(players: Player[]): Player[] {
  return [...players].sort(
    (a, b) => positionRank(a.position) - positionRank(b.position) || a.name.localeCompare(b.name),
  );
}

/**
 * Splits a roster into the buckets the UI shows.
 *
 * `starters` is positionally aligned with the non-bench entries of
 * `roster_positions`, which is how a slot is labelled FLEX vs RB. An empty slot
 * comes back as the string "0", not null.
 */
export interface RosterGroups {
  starters: Array<{ slot: string; player: Player | null }>;
  bench: Player[];
  taxi: Player[];
  ir: Player[];
}

export function groupRoster(
  team: Team,
  rosterPositions: string[],
  players: PlayersFile | null,
): RosterGroups {
  const startingSlots = rosterPositions.filter((p) => p !== 'BN' && p !== 'TAXI' && p !== 'IR');

  const starters = startingSlots.map((slot, i) => {
    const id = team.starters[i];
    return { slot, player: id && id !== '0' ? lookupPlayer(players, id) : null };
  });

  const assigned = new Set<string>([...team.starters, ...team.taxi, ...team.reserve]);
  const bench = team.players
    .filter((id) => !assigned.has(id))
    .map((id) => lookupPlayer(players, id));

  return {
    starters,
    bench: sortPlayers(bench),
    taxi: sortPlayers(team.taxi.map((id) => lookupPlayer(players, id))),
    ir: sortPlayers(team.reserve.map((id) => lookupPlayer(players, id))),
  };
}

/** Count of rostered players at each position -- the dynasty depth chart. */
export function positionCounts(team: Team, players: PlayersFile | null): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const id of team.players) {
    const p = lookupPlayer(players, id);
    counts[p.position] = (counts[p.position] ?? 0) + 1;
  }
  return counts;
}

// --- Draft picks -----------------------------------------------------------

export interface PickSummary {
  /** Picks this roster currently holds, including its own. */
  held: PickAsset[];
  /** Picks originally this roster's that now belong to someone else. */
  lost: PickAsset[];
  /** Picks acquired from other rosters. */
  gained: PickAsset[];
  /** Net count vs. the baseline of owning exactly your own picks. */
  net: number;
}

export function summarizePicks(picks: PickAsset[], rosterId: number): PickSummary {
  const held = picks.filter((p) => p.ownerRosterId === rosterId);
  const lost = picks.filter((p) => p.originalRosterId === rosterId && p.ownerRosterId !== rosterId);
  const gained = picks.filter(
    (p) => p.ownerRosterId === rosterId && p.originalRosterId !== rosterId,
  );
  return { held, lost, gained, net: gained.length - lost.length };
}

export function pickSeasons(picks: PickAsset[]): string[] {
  return [...new Set(picks.map((p) => p.season))].sort();
}
